import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficeNoteRow } from "services/api/offices/types";
import { getOfficeNotesAPI, deleteOfficeNoteAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { Dialog } from "ui/components/Dialog";
import { TableSearch } from "ui/components/Table/TableSearch";
import OfficeNoteEditCard from "./OfficeNoteEditCard/OfficeNoteEditCard";
import { OFFICE_NOTE_COLUMNS } from "./consts";

interface OfficeNoteStepProps {
  formValues: OfficeEditFormValues;
}

const OfficeNoteStep: React.FC<OfficeNoteStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [notes, setNotes] = useState<OfficeNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficeNotesAPI(initialDataWithPagination.pagination, officeId);
      setNotes(result.data);
    } catch {
      setNotes([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchNotes();
  }, [officeId]);

  const handleRowClick = useCallback((noteId: string) => {
    setEditNoteId(noteId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditNoteId(null);
    setShowEdit(true);
  };

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, noteId: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteNoteId(noteId);
    },
    []
  );

  const handleDeleteConfirm = async () => {
    if (!officeId || !deleteNoteId) return;

    await deleteOfficeNoteAPI(t, officeId, deleteNoteId);

    setDeleteNoteId(null);
    fetchNotes();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditNoteId(null);
    fetchNotes();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Bilješke poslovnice</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte bilješke o poslovnici klikom na gumb <strong>"Dodaj bilješku"</strong>.
          </p>

          {/* Uređivanje ide klikom na redak; prazno stanje nudi samo unos. */}
          {!isLoading && notes.length === 0 ? (
            <div className="max-w-md mx-auto">
              <Button
                variant="primary"
                size="full"
                startIcon="/media/icons/plus.svg"
                children="Dodaj bilješku"
                onClick={handleAddNew}
              />
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  children="Dodaj bilješku"
                  onClick={handleAddNew}
                />
              </div>

              <TableSearch
                columns={OFFICE_NOTE_COLUMNS(handleDeleteClick)}
                data={notes}
                selectableRows={false}
                onRowClick={handleRowClick}
                isLoading={isLoading}
                disablePagination
              />
            </>
          )}
        </div>
      </div>

      <OfficeNoteEditCard
        officeId={officeId ?? ""}
        noteId={editNoteId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />

      {/* Bilješka se prije brisala odmah, bez pitanja; sada traži potvrdu kao i ostalo. */}
      <Dialog
        open={!!deleteNoteId}
        title="Brisanje bilješke"
        message="Jeste li sigurni da želite obrisati ovu bilješku?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteNoteId(null)}
      />
    </>
  );
};

export default memo(OfficeNoteStep);
